import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useLang } from "@/i18n";
import PublicLayout from "@/components/layout/PublicLayout";
import ProfileCard from "@/components/ProfileCard";
import { ArrowRight, ShieldCheck, Sparkles, Trophy, ClipboardList, Building2, HeartPulse, Grid3x3 } from "lucide-react";
import { Link } from "react-router-dom";

// LANDING-VISUAL-HOTFIX-01 (Feb 2026) — tennis-youth imagery + grid harmony.
//
// The previous stock-photo gamble misfired (one URL 404'd, another
// returned VOLLEYBALL instead of tennis). To remove that risk entirely
// we generated three editorial tennis-youth photographs with Google
// Gemini Nano Banana (gemini-3.1-flash-image-preview) and shipped them
// as static assets under `frontend/public/landing/`. Generation script
// lives at /app/scripts/gen_landing_tennis_images.py — rerun if we ever
// need different shots.
//
//   1. TENNIS_GROUP_IMG → young tennis players on court
//   2. TENNIS_COACH_IMG → coach + kids listening on court
//   3. TENNIS_FUN_IMG   → kids laughing with rackets (emotional shot)
//   4. MED_IMG          → UNCHANGED (doctor / physical-therapy block,
//                         from Unsplash — explicitly preserved per brief)
//
// Defensive note: the <img> tags below still use `onError` so a
// missing file under /public/landing/ falls back to a soft emerald
// gradient instead of the browser's broken-image icon.
const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/e0edabf1-7bde-4cd6-aa89-d096d095407f/images/a4766d5ba5128c306ed73c2f0a2f419818728de040c9d6c69dea28e870b88183.png";
const TENNIS_GROUP_IMG = "/landing/tennis-group.jpg";
const TENNIS_COACH_IMG = "/landing/tennis-coach.jpg";
const TENNIS_FUN_IMG   = "/landing/tennis-fun.jpg";
const MED_IMG          = "https://images.unsplash.com/photo-1770653927355-2ba81c1522df?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwxfHxzcG9ydHMlMjBtZWRpY2luZSUyMHBoeXNpY2FsJTIwdGhlcmFweXxlbnwwfHx8fDE3Nzc5NzE2Njh8MA&ixlib=rb-4.1.0&q=85";

// Defensive image fallback — when the CDN fails the original URL is
// stashed in a data attribute so we don't loop forever (rare edge case
// where the fallback itself misfires).
const onImgError = (e) => {
  const node = e?.currentTarget;
  if (!node || node.dataset.fallbackApplied === "1") return;
  node.dataset.fallbackApplied = "1";
  // Replace the broken <img> with a soft emerald gradient so the slot
  // never visually breaks the grid composition.
  node.style.background = "linear-gradient(135deg, #d1fae5 0%, #ecfdf5 50%, #f0fdf4 100%)";
  node.removeAttribute("src");
  node.alt = "";
};

const Landing = () => {
  const { t } = useLang();
  const [profiles, setProfiles] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/profiles").then((r) => setProfiles(r.data)).catch(() => {});
  }, []);

  // Honor the #profiles hash anchor when Landing is reached directly from
  // "Change profile" on the Register page (hard navigation) OR via SPA
  // nav. We need to defer past image loading so the section's final
  // position is stable before scrolling — otherwise async hero/profile
  // images shift layout below the scroll and the section ends up
  // mid-viewport instead of flush-top.
  useEffect(() => {
    if (window.location.hash !== "#profiles") return;
    let cancelled = false;
    const scrollToProfiles = () => {
      if (cancelled) return;
      const el = document.getElementById("profiles");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };
    if (document.readyState === "complete") {
      // Everything already loaded — scroll next frame.
      requestAnimationFrame(scrollToProfiles);
    } else {
      // Wait for images/fonts to finish loading, then scroll.
      window.addEventListener("load", scrollToProfiles, { once: true });
    }
    // Belt-and-suspenders: a final scroll after a short delay covers the
    // case where late-loading external images (Pexels/Unsplash) push the
    // layout after `load`.
    const t = setTimeout(scrollToProfiles, 700);
    return () => {
      cancelled = true;
      clearTimeout(t);
      window.removeEventListener("load", scrollToProfiles);
    };
  }, []);

  const onPickProfile = (p) => {
    navigate(`/register?profile=${p.key}`);
  };

  const audienceCards = [
    { icon: Trophy,         color: "bg-amber-50 text-amber-600",     title: t("landing.audienceCard1Title"), body: t("landing.audienceCard1Body") },
    { icon: ClipboardList,  color: "bg-emerald-50 text-emerald-600", title: t("landing.audienceCard2Title"), body: t("landing.audienceCard2Body") },
    { icon: Building2,      color: "bg-sky-50 text-sky-600",         title: t("landing.audienceCard3Title"), body: t("landing.audienceCard3Body") },
    { icon: HeartPulse,     color: "bg-pink-50 text-pink-600",       title: t("landing.audienceCard4Title"), body: t("landing.audienceCard4Body") },
  ];

  return (
    <PublicLayout>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover opacity-50" />
          <div className="absolute inset-0 bg-gradient-to-b from-white via-white/60 to-white" />
        </div>
        <div className="tsw-section pt-16 pb-5 lg:pt-24 lg:pb-7 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 tsw-anim-fade-up">
            <div className="tsw-overline mb-4" data-testid="hero-overline">{t("landing.heroOverline")}</div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 leading-[1.05]" data-testid="hero-title">
              {t("landing.heroTitle")} <span className="text-emerald-600">{t("landing.heroTitleAccent")}</span>
            </h1>
            <p className="mt-5 text-lg text-slate-600 max-w-2xl leading-relaxed" data-testid="hero-sub">{t("landing.heroSub")}</p>
            <p className="mt-3 text-base text-slate-700 max-w-2xl leading-relaxed font-medium" data-testid="hero-support">{t("landing.heroSupport")}</p>
            <p className="mt-3 text-sm text-slate-500 max-w-2xl leading-relaxed border-s-2 border-emerald-200 ps-4 italic" data-testid="hero-invite">{t("landing.heroInvite")}</p>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <a href="#profiles" className="tsw-btn-primary" data-testid="hero-cta-join">
                {t("landing.ctaJoin")} <ArrowRight className="w-4 h-4 rtl:rotate-180" />
              </a>
              <a href="#audience" className="tsw-btn-secondary" data-testid="hero-cta-learn">
                {t("landing.ctaLearn")}
              </a>
              {/* MOBILE-FIX-01 — Tertiary CTA to the TEAM OS Hub. The
                  Navbar already exposes a Hub icon button on mobile, but
                  a discoverable in-flow CTA here helps brand-new
                  visitors who scan the landing top-to-bottom and never
                  look at the header. Styled distinctly from the two
                  primary actions so it doesn't compete with "Join the
                  pilot" but stays prominent enough to be tap-friendly. */}
              <Link
                to="/apps"
                className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50/70 px-4 py-2 text-sm font-semibold text-emerald-800 hover:bg-emerald-100 transition w-full sm:w-auto justify-center sm:justify-start"
                data-testid="hero-cta-apps-hub"
              >
                <Grid3x3 className="w-4 h-4" /> {t("apps.heroBadge")}
              </Link>
            </div>
            <div className="mt-8 flex items-start gap-4 text-sm text-slate-500">
              <ShieldCheck className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
              <span>{t("landing.footerNote")}</span>
            </div>
          </div>

          {/* LANDING-VISUAL-HOTFIX-01 — Hero collage redesigned as a
              symmetric 2×3 grid (mobile 2-col / lg 5-col-span):

                ┌──────────┬──────────┐
                │ tennis 1 │ tennis 2 │  ← young players  · coach + kids
                ├──────────┼──────────┤
                │ tennis 3 │  doctor  │  ← kids laughing  · medical (kept)
                ├──────────┴──────────┤
                │   "Gana recompensas" badge card     │  ← spans both cols
                └─────────────────────────────────────┘

              Equal-height tiles + one wide accent card at the bottom
              recovers the visual hierarchy that the previous layout
              broke. The badge card now sits anchored at the foot, with
              its full width, instead of floating awkwardly between
              images. */}
          <div className="lg:col-span-5 grid grid-cols-2 gap-3 sm:gap-4">
            <img
              src={TENNIS_GROUP_IMG}
              alt=""
              loading="lazy"
              onError={onImgError}
              className="rounded-2xl object-cover h-44 sm:h-52 lg:h-56 w-full border border-slate-100"
              data-testid="hero-img-tennis-1"
            />
            <img
              src={TENNIS_COACH_IMG}
              alt=""
              loading="lazy"
              onError={onImgError}
              className="rounded-2xl object-cover h-44 sm:h-52 lg:h-56 w-full border border-slate-100"
              data-testid="hero-img-tennis-2"
            />
            <img
              src={TENNIS_FUN_IMG}
              alt=""
              loading="lazy"
              onError={onImgError}
              className="rounded-2xl object-cover h-44 sm:h-52 lg:h-56 w-full border border-slate-100"
              data-testid="hero-img-tennis-3"
            />
            <img
              src={MED_IMG}
              alt=""
              loading="lazy"
              onError={onImgError}
              className="rounded-2xl object-cover h-44 sm:h-52 lg:h-56 w-full border border-slate-100"
              data-testid="hero-img-doctor"
            />
            <div
              className="col-span-2 rounded-2xl border border-emerald-100 bg-gradient-to-br from-emerald-50 to-emerald-100/50 p-5 flex items-center gap-4"
              data-testid="hero-rewards-badge"
            >
              <div className="w-12 h-12 rounded-xl bg-white/80 border border-emerald-200 flex items-center justify-center shrink-0">
                <Sparkles className="w-6 h-6 text-emerald-600" />
              </div>
              <div className="text-sm sm:text-base text-emerald-900 leading-snug font-medium">
                {t("landing.heroBadge")}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Audience */}
      <section id="audience" className="py-5 sm:py-7 bg-slate-50/60">
        <div className="tsw-section">
          <div className="max-w-3xl mb-4 sm:mb-5">
            <div className="tsw-overline mb-2">{t("landing.step01")}</div>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight font-display" data-testid="audience-title">{t("landing.audienceTitle")}</h2>
            <p className="text-slate-600 mt-3 leading-relaxed" data-testid="audience-intro">{t("landing.audienceIntro")}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="audience-grid">
            {audienceCards.map((p) => (
              <div key={p.title} className="tsw-card p-6">
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${p.color}`}>
                  <p.icon className="w-5 h-5" />
                </div>
                <div className="mt-4 font-display font-semibold text-slate-900 text-lg leading-snug">{p.title}</div>
                <p className="text-slate-600 mt-1.5 text-sm leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Profile selector */}
      <section id="profiles" className="py-5 sm:py-7 bg-white">
        <div className="tsw-section">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-4 sm:mb-5">
            <div>
              <div className="tsw-overline mb-2">{t("landing.step02")}</div>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight font-display">{t("landing.profileSelectorTitle")}</h2>
              <p className="text-slate-600 mt-2 max-w-2xl">{t("landing.profileSelectorSub")}</p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="profile-grid">
            {profiles.map((p) => (
              <ProfileCard key={p.key} profile={p} onSelect={onPickProfile} />
            ))}
          </div>
        </div>
      </section>

      {/* Closing */}
      <section className="py-20 bg-gradient-to-br from-emerald-50 via-white to-sky-50/40">
        <div className="tsw-section grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8">
            <div className="tsw-overline mb-3">{t("landing.step03")}</div>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight font-display" data-testid="closing-title">{t("landing.closingTitle")}</h2>
            <p className="text-slate-700 mt-4 text-lg leading-relaxed max-w-3xl" data-testid="closing-body">{t("landing.closingBody")}</p>
          </div>
          <div className="lg:col-span-4 flex lg:justify-end">
            <a href="#profiles" className="tsw-btn-primary" data-testid="closing-cta-join">
              {t("landing.ctaJoin")} <ArrowRight className="w-4 h-4 rtl:rotate-180" />
            </a>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
};

export default Landing;
